import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  X, ChevronRight, Trophy, Star, TrendingUp, Zap, Award, Users, Sparkles, Share2,
} from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { SeasonWrappedData, WrappedSlide } from '../types/dugout';
import { generateWrapped } from '../lib/wrapped/wrapped-client';
import { shareWrappedCard } from '../lib/wrapped/wrapped-share';
import { RotatingStatus } from './RotatingStatus';

// Wrapped renders as a dark, full-screen "story". On the app's .light theme the
// bg-ink below is a true near-black backdrop; all text uses explicit light
// hex so nothing falls into the theme-remap white-on-white trap.
//
// ⚠ DARK-SURFACE EXCEPTION. The semantic tokens (ink / ink-2 / accent-ink /
// gold-ink / danger) are tuned for contrast on the LIGHT canvas, so their
// "ink" variants are wrong here — accent-ink is 3.3:1 on this backdrop while
// the bright accent is 6.9:1. On these slides: text-surface is the light text,
// and fills (accent, gold, #FCD34D) are the bright variants. Do not sweep this
// file with a light-surface token migration.

interface Props {
  team: any;
  players: any[];
  /** All games already loaded by the parent (current + past). */
  games: any[];
  onClose: () => void;
}

const GENERATING_LINES = [
  'Reading the scorebook…',
  'Counting the clutch hits…',
  'Finding the biggest moment…',
  'Tallying the season…',
  'Cueing the highlight reel…',
  'Wrapping it all up…',
];

export const SeasonWrappedModal = ({ team, players, games, onClose }: Props) => {
  const [data, setData] = useState<SeasonWrappedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [sharing, setSharing] = useState(false);
  const shareCardRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await generateWrapped(team, players, games);
        if (cancelled) return;
        setData(result);
        // Persist a lightweight marker so we know this season's Wrapped has been
        // generated (additive, merge-only — touches nothing else on the team doc).
        try {
          await setDoc(
            doc(db, 'teams', team.id),
            { wrapped: { seasonKey: result.seasonKey, generatedAt: result.generatedAt } },
            { merge: true },
          );
        } catch { /* marker is non-critical; Wrapped still shows */ }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not build your Wrapped.');
      }
    })();
    return () => { cancelled = true; };
  }, [team?.id]);

  const slides = data?.slides ?? [];
  const atEnd = index >= slides.length - 1;
  const next = () => setIndex(i => Math.min(i + 1, slides.length - 1));
  const prev = () => setIndex(i => Math.max(i - 1, 0));

  const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -40) next();
    else if (dx > 40) prev();
    touchStartX.current = null;
  };

  const handleShare = async () => {
    if (!data || !shareCardRef.current) return;
    setSharing(true);
    try {
      await shareWrappedCard(shareCardRef.current, data);
    } catch {
      /* share is best-effort; swallow */
    } finally {
      setSharing(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] bg-ink flex flex-col safe-top overflow-hidden"
      >
        {/* Close + (when ready) share */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
          <button onClick={onClose} className="p-2 -ml-2 text-[#9AA0A6] hover:text-surface transition-colors">
            <X className="w-6 h-6" />
          </button>
          {data && (
            <button
              onClick={handleShare}
              disabled={sharing}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1C2127] text-surface font-black text-[11px] uppercase italic tracking-widest hover:bg-[#262C34] transition-colors disabled:opacity-50"
            >
              <Share2 className="w-4 h-4" />
              <span>{sharing ? 'Saving…' : 'Share'}</span>
            </button>
          )}
        </div>

        {/* Progress segments (story-style) */}
        {slides.length > 0 && (
          <div className="flex gap-1.5 px-5 pb-3 shrink-0">
            {slides.map((_, i) => (
              <div key={i} className="h-1 flex-1 rounded-full overflow-hidden bg-[#2A2F36]">
                <div
                  className="h-full bg-[#FCD34D] transition-all duration-300"
                  style={{ width: i < index ? '100%' : i === index ? '100%' : '0%' }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Body */}
        <div
          className="flex-1 relative"
          onClick={(e) => {
            // Tap right half = next, left quarter = prev (story convention).
            const x = e.clientX;
            const w = window.innerWidth;
            if (x < w * 0.25) prev();
            else if (!atEnd) next();
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {!data && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2.5, ease: 'linear' }}
              >
                <Sparkles className="w-12 h-12 text-[#FCD34D]" />
              </motion.div>
              <RotatingStatus messages={GENERATING_LINES} />
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
              <p className="text-surface font-black italic uppercase mb-2">Couldn't build your Wrapped</p>
              <p className="text-[#9AA0A6] text-sm mb-6">{error}</p>
              <button onClick={onClose} className="px-6 py-3 rounded-2xl bg-[#FCD34D] text-ink font-black uppercase italic">
                Close
              </button>
            </div>
          )}

          {data && (
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center"
              >
                <Slide slide={slides[index]} data={data} />
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Footer nav */}
        {data && (
          <div className="px-6 pb-8 pt-2 shrink-0">
            {atEnd ? (
              <button
                onClick={onClose}
                className="w-full py-4 rounded-2xl bg-[#FCD34D] text-ink font-black uppercase italic tracking-wide flex items-center justify-center gap-2 hover:brightness-105 transition"
              >
                <span>That's a Wrap</span>
                <Sparkles className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={next}
                className="w-full py-4 rounded-2xl bg-[#1C2127] text-surface font-black uppercase italic tracking-wide flex items-center justify-center gap-2 hover:bg-[#262C34] transition"
              >
                <span>Next</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Off-screen share card (rendered for html-to-image capture) */}
        {data && (
          <div className="fixed -left-[9999px] top-0" aria-hidden>
            <ShareCard ref={shareCardRef} data={data} />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

// ── Per-slide renderers ──────────────────────────────────────────────────────

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[#FCD34D] font-semibold uppercase tracking-[0.2em] text-xs mb-4">{children}</p>
);
const BigStat = ({ children }: { children: React.ReactNode }) => (
  <p className="text-surface font-black italic tracking-tighter leading-none text-7xl mb-4">{children}</p>
);
const Prose = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[#C8CDD2] text-base leading-relaxed max-w-sm mx-auto">{children}</p>
);
const Transition = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[#7A828B] text-xs uppercase tracking-widest font-semibold mt-6">{children}</p>
);

const SUPERLATIVE_ICON: Record<string, React.ElementType> = {
  clutch: Zap, leader: Trophy, most_improved: TrendingUp, workhorse: Award,
  grinder: Star, glove: Award, game_balls: Award, personal_best: Star,
};

function Slide({ slide, data }: { slide: WrappedSlide; data: SeasonWrappedData }) {
  switch (slide.kind) {
    case 'intro':
      return (
        <>
          <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ repeat: Infinity, duration: 3 }}>
            <Sparkles className="w-16 h-16 text-[#FCD34D] mb-8" />
          </motion.div>
          <h1 className="text-5xl font-black italic uppercase tracking-tighter text-surface mb-3">{slide.title}</h1>
          <p className="text-[#9AA0A6] font-black uppercase tracking-[0.2em] text-sm">{slide.subtitle}</p>
        </>
      );

    case 'record':
      return (
        <>
          <Eyebrow>The Final Word</Eyebrow>
          <BigStat>
            {slide.wins}-{slide.losses}{slide.ties > 0 ? `-${slide.ties}` : ''}
          </BigStat>
          <Prose>{slide.thesis}</Prose>
        </>
      );

    case 'runDiff':
      return (
        <>
          <Eyebrow>Runs</Eyebrow>
          <div className="flex items-end justify-center gap-6 mb-6">
            <div>
              {/* bright accent, not accent-ink: this slide sits on the near-black
                  Wrapped backdrop, where the dark green drops to 3.3:1 */}
              <p className="text-accent font-black italic text-5xl leading-none">{slide.runsFor}</p>
              <p className="text-[#7A828B] text-[10px] uppercase tracking-widest font-semibold mt-2">Scored</p>
            </div>
            <div className="text-[#3A4049] font-black text-3xl pb-2">/</div>
            <div>
              <p className="text-[#F87171] font-black italic text-5xl leading-none">{slide.runsAgainst}</p>
              <p className="text-[#7A828B] text-[10px] uppercase tracking-widest font-semibold mt-2">Allowed</p>
            </div>
          </div>
          <p className="text-surface font-black italic uppercase tracking-tight text-lg">
            {slide.diff >= 0 ? '+' : ''}{slide.diff} run differential
          </p>
          {slide.oneRunRecord !== '0-0' && (
            <Prose>And {slide.oneRunRecord} in the one-run nailbiters.</Prose>
          )}
          <Transition>{slide.transition}</Transition>
        </>
      );

    case 'highlights':
      return (
        <>
          <Eyebrow>Season Highlights</Eyebrow>
          <div className="space-y-4 mb-2">
            {slide.lines.map((line, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 * i }}
                className="text-surface font-black italic uppercase tracking-tight text-xl"
              >
                {line}
              </motion.p>
            ))}
          </div>
          <Transition>{slide.transition}</Transition>
        </>
      );

    case 'battingLeaders':
    case 'pitchingLeaders':
      return (
        <>
          <Eyebrow>{slide.kind === 'battingLeaders' ? 'At the Plate' : 'On the Mound'}</Eyebrow>
          <div className="w-full max-w-sm space-y-3 mb-2">
            {slide.rows.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i }}
                className="flex items-center justify-between bg-[#161B21] rounded-2xl px-5 py-3"
              >
                <div className="text-left">
                  <p className="text-[#7A828B] text-[10px] uppercase tracking-widest font-semibold">{r.label}</p>
                  <p className="text-surface font-black italic uppercase tracking-tight">{r.playerName}</p>
                </div>
                <p className="text-[#FCD34D] font-black italic text-xl">{r.value}</p>
              </motion.div>
            ))}
          </div>
          <Transition>{slide.transition}</Transition>
        </>
      );

    case 'superlative': {
      const Icon = SUPERLATIVE_ICON[slide.superlative.angle] || Star;
      return (
        <>
          <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 11 }}>
            <Icon className="w-14 h-14 text-[#FCD34D] mb-6" />
          </motion.div>
          <Eyebrow>{slide.superlative.title}</Eyebrow>
          <h2 className="text-4xl font-black italic uppercase tracking-tighter text-surface mb-1">
            {slide.superlative.playerName}
          </h2>
          <p className="text-[#FCD34D] font-black italic text-lg mb-4">{slide.superlative.statLine}</p>
          <Prose>{slide.superlative.prose}</Prose>
        </>
      );
    }

    case 'biggestMoment':
      return (
        <>
          <div className="relative mb-6">
            <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
              <Zap className="w-16 h-16 text-[#FCD34D]" />
            </motion.div>
          </div>
          <Eyebrow>Biggest Moment</Eyebrow>
          <h2 className="text-3xl font-black italic uppercase tracking-tighter text-surface mb-1">
            {slide.moment.playerName}
          </h2>
          <p className="text-[#C8CDD2] font-bold uppercase tracking-wide text-sm mb-4">{slide.moment.description}</p>
          <Prose>{slide.moment.prose}</Prose>
        </>
      );

    case 'clutchPlayer':
      return (
        <>
          <Zap className="w-14 h-14 text-[#FCD34D] mb-6" />
          <Eyebrow>Mr. Clutch</Eyebrow>
          <h2 className="text-4xl font-black italic uppercase tracking-tighter text-surface mb-1">
            {slide.clutch.playerName}
          </h2>
          <p className="text-[#FCD34D] font-black italic text-lg mb-4">{slide.clutch.statLine}</p>
          <Prose>{slide.clutch.prose}</Prose>
          <p className="text-[#5A626B] text-[10px] leading-snug max-w-xs mx-auto mt-6">{slide.footnote}</p>
        </>
      );

    case 'coachCard':
      return (
        <>
          <Users className="w-14 h-14 text-[#FCD34D] mb-6" />
          <Eyebrow>From the Dugout, Coach</Eyebrow>
          <div className="w-full max-w-sm grid grid-cols-2 gap-3 mb-2">
            {slide.rows.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i }}
                className="bg-[#161B21] rounded-2xl px-4 py-4"
              >
                <p className="text-[#FCD34D] font-black italic text-3xl leading-none">{r.value}</p>
                <p className="text-[#7A828B] text-[10px] uppercase tracking-widest font-semibold mt-2">{r.label}</p>
              </motion.div>
            ))}
          </div>
          <Transition>{slide.transition}</Transition>
        </>
      );

    case 'closing':
      return (
        <>
          <div className="relative mb-8">
            <Trophy className="w-20 h-20 text-[#FCD34D]" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {[...Array(10)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, x: 0, y: 0 }}
                  animate={{
                    scale: [0, 1, 0],
                    x: Math.cos((i * 36 * Math.PI) / 180) * 120,
                    y: Math.sin((i * 36 * Math.PI) / 180) * 120,
                  }}
                  transition={{ repeat: Infinity, duration: 1.8, delay: i * 0.12 }}
                >
                  <Star className="w-4 h-4 text-[#FCD34D] fill-current" />
                </motion.div>
              ))}
            </div>
          </div>
          <Prose>{slide.note}</Prose>
        </>
      );

    default:
      return null;
  }
}

// ── Share card (1080-ish vertical, captured to PNG) ──────────────────────────
const ShareCard = React.forwardRef<HTMLDivElement, { data: SeasonWrappedData }>(({ data }, ref) => {
  const topSup = data.superlatives.slice(0, 3);
  return (
    <div
      ref={ref}
      style={{ width: 540, height: 960 }}
      className="bg-ink flex flex-col items-center justify-between p-10 text-center"
    >
      <div className="pt-6">
        <p className="text-[#FCD34D] font-black uppercase tracking-[0.2em] text-sm">
          {data.season} {data.seasonYear} Wrapped
        </p>
        <h1 className="text-5xl font-black italic uppercase tracking-tighter text-surface mt-3">{data.teamName}</h1>
      </div>

      <div>
        <p className="text-surface font-black italic tracking-tighter text-8xl leading-none">
          {data.record.wins}-{data.record.losses}{data.record.ties > 0 ? `-${data.record.ties}` : ''}
        </p>
        <p className="text-[#9AA0A6] text-base leading-relaxed max-w-xs mx-auto mt-4">{data.thesis}</p>
      </div>

      <div className="w-full space-y-2">
        {topSup.map((s) => (
          <div key={s.playerId} className="flex items-center justify-between bg-[#161B21] rounded-2xl px-5 py-3">
            <div className="text-left">
              <p className="text-[#7A828B] text-[10px] uppercase tracking-widest font-semibold">{s.title}</p>
              <p className="text-surface font-black italic uppercase tracking-tight">{s.playerName}</p>
            </div>
            <p className="text-[#FCD34D] font-black italic">{s.statLine}</p>
          </div>
        ))}
      </div>

      <p className="text-[#5A626B] font-semibold uppercase tracking-[0.25em] text-xs pb-4">Dugout</p>
    </div>
  );
});
ShareCard.displayName = 'WrappedShareCard';
